/**
 * Users API Client
 *
 * Client-side API functions for user management operations.
 * All operations go through server-side API endpoints.
 */

import { apiFetch } from '@/lib/api-client';
import logger from '@/lib/logging';
import {
  createUsersRolesTrace,
  logUsersRolesDebug,
  parseFetchErrorBody,
  logFetchResult,
} from '@/lib/users-roles-debug';
import type { EmailKeyUserRow, UsersListResponse } from '@/lib/users-roles/types';
import { formatExplainAccess } from '@/lib/users-roles/explain-access';
import type { ExplainAccessInput } from '@/lib/users-roles/types';

/** Server list can exceed 45s under emulator load; avoid aborting and starting a duplicate request. */
const USERS_LIST_FETCH_TIMEOUT_MS = 180_000;

let inflightUsersFetch: Promise<UsersListResponse> | null = null;
let cachedEtag: string | null = null;

export type { EmailKeyUserRow, UsersListResponse };

/**
 * Fetch all users keyed by Email key (one row per email, no pre_ twins).
 * Sends If-None-Match when a prior ETag is known; 304 yields cached rows from the caller.
 */
export async function fetchAllUsers(options?: {
  includeDeleted?: boolean;
  /** Skip in-flight deduplication (e.g. after a mutation). */
  force?: boolean;
  etag?: string | null;
}): Promise<UsersListResponse> {
  if (options?.force) {
    cachedEtag = null;
  }

  if (!options?.force && !options?.includeDeleted && inflightUsersFetch) {
    return inflightUsersFetch;
  }

  const fetchPromise = fetchAllUsersOnce(options);
  if (!options?.force && !options?.includeDeleted) {
    inflightUsersFetch = fetchPromise.finally(() => {
      inflightUsersFetch = null;
    });
    return inflightUsersFetch;
  }
  return fetchPromise;
}

async function fetchAllUsersOnce(options?: {
  includeDeleted?: boolean;
  etag?: string | null;
}): Promise<UsersListResponse> {
  const traceId = createUsersRolesTrace('fetchAllUsers');
  const started = performance.now();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), USERS_LIST_FETCH_TIMEOUT_MS);
  const etag =
    options && Object.prototype.hasOwnProperty.call(options, 'etag')
      ? (options.etag ?? undefined)
      : (cachedEtag ?? undefined);

  try {
    const query = options?.includeDeleted ? '?includeDeleted=true' : '';
    const response = await fetch(`/api/protected/admin/users${query}`, {
      credentials: 'include',
      signal: controller.signal,
      headers: etag ? { 'If-None-Match': etag } : undefined,
    });

    const elapsedMs = Math.round(performance.now() - started);

    if (response.status === 304) {
      logUsersRolesDebug('users.list', `${traceId} not-modified`, { elapsedMs, etag });
      return {
        success: true,
        data: [],
        count: 0,
        fallbackHits: 0,
        etag: etag ?? '',
        notModified: true,
      } as UsersListResponse & { notModified?: boolean };
    }

    if (!response.ok) {
      const parsed = await parseFetchErrorBody(response);
      logFetchResult(traceId, 'users.list', response, parsed, { elapsedMs });
      throw new Error(
        parsed.message || parsed.error || `Failed to fetch users: ${response.status} ${response.statusText}`,
      );
    }

    const result = (await response.json()) as UsersListResponse;
    const responseEtag = response.headers.get('ETag');
    if (responseEtag) {
      cachedEtag = responseEtag;
    }
    logUsersRolesDebug('users.list', `${traceId} ok`, {
      elapsedMs,
      count: result.count ?? result.data.length,
      fallbackHits: result.fallbackHits,
      withEmail: result.data.filter((u) => u.email).length,
    });
    return { ...result, etag: responseEtag ?? result.etag };
  } catch (error) {
    const message =
      error instanceof Error && error.name === 'AbortError'
        ? `Timed out loading users after ${USERS_LIST_FETCH_TIMEOUT_MS / 1000}s`
        : error instanceof Error
          ? error.message
          : String(error);
    logUsersRolesDebug('users.list', `${traceId} failed`, {
      elapsedMs: Math.round(performance.now() - started),
      error: message,
    });
    logger.error('Error fetching users:', error);
    throw new Error(message);
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function fetchExplainAccess(emailKey: string): Promise<string> {
  const response = await fetch(
    `/api/protected/admin/users/explain?emailKey=${encodeURIComponent(emailKey)}`,
    { credentials: 'include' },
  );
  if (!response.ok) {
    throw new Error('Failed to load access diagnostic');
  }
  const body = (await response.json()) as { diagnostic: ExplainAccessInput; text?: string };
  return body.text ?? formatExplainAccess(body.diagnostic);
}

export async function restoreEmailKeyApi(emailKey: string): Promise<void> {
  try {
    await apiFetch(`/admin/users/${encodeURIComponent(emailKey)}/restore`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  } catch (error) {
    throw new Error(error instanceof Error ? error.message : 'Restore failed');
  }
}

export async function migrateEmailKeyApi(
  emailKey: string,
): Promise<{ ok: true } | { ok: false; status: number; code?: string; message: string }> {
  try {
    await apiFetch(`/admin/users/${encodeURIComponent(emailKey)}/migrate`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
    return { ok: true };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Migrate failed';
    const statusMatch = message.match(/^HTTP (\d+):/);
    const status = statusMatch ? Number(statusMatch[1]) : 500;
    const codeMatch = message.match(/\b(offboarded|not-invited|forbidden)\b/);
    return { ok: false, status, code: codeMatch?.[1], message };
  }
}

/** @deprecated Replaced by on-demand refresh + ETag (ADR-0022). */
export function pollUsers(): () => void {
  logUsersRolesDebug('users.poll', 'pollUsers is retired — no-op', {});
  return () => undefined;
}
